import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import { formatDecimal, formatUnit } from '@/common/utils/formatters'
import { requiredFieldStrategy } from '@/features/equipmentCategories/constants/requiredFieldOptions.constant'
import { unitForConsumptionStrategy } from '@/features/equipmentCategories/constants/unitForConsumptionOptions.constants'
import { useEquipmentCategory } from '@/features/equipmentCategories/hooks/useEquipmentCategory'
import { BoxesIcon, ChevronsRight } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'

const EquipmentCategoriesShow = () => {
  const { id } = useParams()

  const {
    data: equipmentCategory,
    isLoading,
    isPending,
    error: requestError = {} as any,
    refetch: refetchRequest,
  } = useEquipmentCategory(id || '')

  const driverLicensesCategories = equipmentCategory?.driver_license_category_equipment_categories ?? []
  const hasLicensesCategories = driverLicensesCategories.length > 0

  const disableUpdateButton = isPending || requestError?.status === 403;

  return (
    <div className="flex flex-col w-full gap-y-6">
      <div className="flex justify-between items-center">
        <div className="flex gap-2 items-center">
          <GoBackButton fallback="/equipment-categories" searchLastRouteFromFallback />
          <p className="text-white-400 font-normal">Categorias de equipamento</p>
          <ChevronsRight color="#ffffffb3" />
          <h2>{equipmentCategory?.display_name}</h2>
        </div>

        <Link to={`/equipment-categories/${id}/edit`}>
          <Button
            name="access_equipment_categories_show"
            operationKlassToCheckPermission="Admin::EquipmentCategories::Update"
            disabled={disableUpdateButton}
          >
            Editar
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <Box>Carregando...</Box>
      ) : (
        <ErrorMessageWrapper
          className="max-h-[calc(100vh-128px)]"
          statusCode={requestError?.status}
          refetchFunction={refetchRequest}
        >
          <Box className="flex max-h-[calc(100vh-128px)] flex-col gap-y-4 overflow-y-auto">
            <p className="text-lg font-bold">Categorias de equipamento</p>

            <Box className="flex flex-col gap-y-2 rounded-lg p-4">
              <p className="text-[0.625rem] text-white-800">Nome</p>

              <p className="text-xs text-white-800" data-testid="display_name">
                {equipmentCategory?.display_name}
              </p>
            </Box>

            <Box className="flex flex-col gap-y-2 rounded-lg p-4">
              <p className="text-[0.625rem] text-white-800">Descrição</p>

              <p className="text-xs text-white-800" data-testid="description">
                {equipmentCategory?.description}
              </p>
            </Box>

            <Box className="flex flex-col gap-y-2 rounded-lg p-4">
              <p className="text-[0.625rem] text-white-800">Porte</p>

              <p className="text-xs text-white-800" data-testid="description">
                {equipmentCategory?.equipment_tier?.display_name || '--'}
              </p>
            </Box>

            <Box className="flex flex-col gap-y-2 rounded-lg p-4">
              <p className="text-[0.625rem] text-white-800">Campos obrigatórios para cadastro de equipamento</p>

              <div className="flex flex-wrap gap-2">
                {equipmentCategory?.required_field_names?.map((field) => (
                  <div key={field} className="flex gap-x-1 items-center bg-white-200 rounded-sm p-2">
                    <BoxesIcon className="size-6 text-white-800" strokeWidth={1} />

                    <p className="text-xs font-bold text-white-800">
                      {requiredFieldStrategy[field as keyof typeof requiredFieldStrategy]}
                    </p>
                  </div>
                ))}
              </div>
            </Box>

            {hasLicensesCategories && (
              <Box className="flex flex-col gap-y-2 rounded-lg p-4">
                <p className="text-[0.625rem] text-white-800">Categorias de CNH aceitas</p>

                <div className="flex flex-wrap gap-2">
                  {equipmentCategory?.driver_license_category_equipment_categories?.map((field, i) => (
                    <div key={i} className="flex gap-x-1 items-center bg-white-200 rounded-sm p-2">
                      <p className="text-xs font-bold text-white-800">
                        {field.driver_license_category.display_name}
                      </p>
                    </div>
                  ))}
                </div>
              </Box>
            )}

            <div className="flex gap-x-4 w-full">
              <Box className="flex flex-col w-full gap-y-2 rounded-lg p-4">
                <p className="text-[0.625rem] text-white-800">Tipo de identificação</p>

                <p className="text-xs text-white-800">
                  {requiredFieldStrategy[equipmentCategory?.field_for_display as keyof typeof requiredFieldStrategy]}
                </p>
              </Box>

              <Box className="flex flex-col w-full gap-y-2 rounded-lg p-4">
                <p className="text-[0.625rem] text-white-800">Métrica da categoria</p>

                <p className="text-xs text-white-800">
                  {
                    unitForConsumptionStrategy[
                    equipmentCategory?.unit_for_consumption as keyof typeof unitForConsumptionStrategy
                    ]
                  }
                </p>
              </Box>
            </div>

            <Box className="flex flex-col w-full gap-y-2 rounded-lg p-4">
              <p className="text-[0.625rem] text-white-800">Capacidade máxima do tanque</p>

              <p className="text-xs text-white-800" data-testid="max_tank_capacity">
                {formatUnit(formatDecimal(equipmentCategory?.max_tank_capacity || '', 0), 'liter')}
              </p>
            </Box>

            <Checkbox label="É uma categoria presente na FIPE" disabled />

            <Checkbox
              label="É uma categoria que suporta INFOCAR"
              checked={equipmentCategory?.supports_infocar_data_enrichment}
              disabled
            />

            <Checkbox
              label="É uma categoria que não precisa de controle por quilometragem/hora uso"
              checked={equipmentCategory?.bypass_consumption_usage_validations}
              disabled
            />
          </Box>
        </ErrorMessageWrapper>
      )}
    </div>
  )
}

export default EquipmentCategoriesShow
